import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Upload, Trash2, Home, LayoutDashboard, LogOut, User, Users, Settings, Key, FileText, Menu, X, Activity, Filter, ScrollText, CircleAlert, ScanLine } from 'lucide-react';
import { authService } from '../services/authService';
import { User as UserType } from '../types/user';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      const user = await authService.getCurrentUser();
      setCurrentUser(user);
    };
    loadUser();
  }, []);

  // const permissions = authService.getUserPermissions(currentUser); // Reserved for future use

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/', label: 'Documents', icon: Home },
    { path: '/upload', label: 'Upload', icon: Upload },
    { path: '/deleted', label: 'Deleted Items', icon: Trash2 },
    { path: '/issues', label: 'Report Issues', icon: CircleAlert },
  ];

  // Admin-only menu items
  const adminItems = currentUser?.role === 'admin' ? [
    { path: '/users', label: 'User Management', icon: Users },
    { path: '/throttling', label: 'Throttling', icon: Settings },
    { path: '/templates', label: 'Templates', icon: FileText },
    { path: '/card-view-config', label: 'Card View Settings', icon: Settings },
    { path: '/logs', label: 'Logs', icon: ScrollText },
    { path: '/one-time-activities', label: 'One-Time Activities', icon: Activity },
    { path: '/search-filter-controls', label: 'Search Filter Controls', icon: Filter },
    { path: '/detection-techniques', label: 'Detection Techniques', icon: ScanLine },
  ] : [];

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Keyboard shortcuts
  useKeyboardShortcuts([
    {
      key: 'k',
      ctrl: true,
      action: () => {
        const searchInput = document.querySelector<HTMLInputElement>('input[type="text"][placeholder*="Search"]');
        searchInput?.focus();
      },
      description: 'Focus search',
    },
    {
      key: '/',
      ctrl: true,
      action: () => setShowShortcuts(true),
      description: 'Show shortcuts',
    },
    {
      key: 'Escape',
      action: () => {
        setSidebarOpen(false);
        setShowShortcuts(false);
      },
      description: 'Close modals/sidebar',
    },
  ]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="relative z-50 bg-gradient-to-r from-primary-600 to-primary-700 shadow-lg border-b border-primary-800 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center min-h-16 h-16">
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button
                type="button"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden mr-3 inline-flex items-center justify-center min-w-[44px] min-h-[44px] p-2 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm mr-3">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-white">Scan-Store-Retrieve</h1>
                <p className="hidden md:block text-xs text-primary-100">Pharma Document Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 lg:space-x-3">
              <div className="hidden sm:flex items-center px-3 py-1.5 bg-white/10 rounded-lg backdrop-blur-sm text-sm text-white">
                <User className="w-4 h-4 mr-2" />
                <span>{currentUser?.username}</span>
                {currentUser?.role === 'admin' && (
                  <span className="ml-2 px-2 py-0.5 bg-accent-500 text-white text-xs rounded-full font-medium">
                    Admin
                  </span>
                )}
              </div>
              <Link
                to="/change-password"
                className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] px-2 lg:px-3 py-2 text-sm text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
                aria-label="Change password"
              >
                <Key className="w-5 h-5 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden lg:inline">Change Password</span>
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] px-2 lg:px-3 py-2 text-sm text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
                aria-label="Log out"
              >
                <LogOut className="w-5 h-5 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden lg:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-x-0 bottom-0 z-30 bg-black/50 transition-opacity top-[calc(4rem+env(safe-area-inset-top,0px))]"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <aside
          className={`
            fixed lg:static left-0 z-40 w-64 bg-white shadow-lg lg:shadow-medium
            top-[calc(4rem+env(safe-area-inset-top,0px))] bottom-0 lg:top-auto lg:bottom-auto lg:inset-y-0
            transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:translate-x-0 transition-transform duration-300 ease-in-out
            min-h-0 lg:min-h-[calc(100vh-4rem)] border-r border-gray-100
            pb-[env(safe-area-inset-bottom,0px)]
          `}
        >
          <nav className="p-4">
            <ul className="space-y-1">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`group flex items-center px-4 py-3 rounded-lg transition-all duration-200 ${
                        isActive
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md transform scale-[1.02]'
                          : 'text-gray-700 hover:bg-primary-50 hover:text-primary-600'
                      }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <Icon className={`w-5 h-5 mr-3 transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                      <span className="font-medium">{item.label}</span>
                      {isActive && (
                        <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse-slow" />
                      )}
                    </Link>
                  </li>
                );
              })}
              {adminItems.length > 0 && (
                <>
                  <li className="pt-4 mt-4 border-t border-gray-200">
                    <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Administration</p>
                  </li>
                  {adminItems.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = item.path === '/logs'
                      ? location.pathname.startsWith('/logs')
                      : location.pathname === item.path;
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`group flex items-center px-4 py-3 rounded-lg transition-all duration-200 ${
                            isActive
                              ? 'bg-gradient-to-r from-accent-500 to-accent-600 text-white shadow-md transform scale-[1.02]'
                              : 'text-gray-700 hover:bg-accent-50 hover:text-accent-600'
                          }`}
                          style={{ animationDelay: `${(navItems.length + index) * 50}ms` }}
                        >
                          <Icon className={`w-5 h-5 mr-3 transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                          <span className="font-medium">{item.label}</span>
                          {isActive && (
                            <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse-slow" />
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </>
              )}
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <main id="main-content" className="flex-1 min-w-0 w-full p-4 lg:p-6 animate-fade-in pb-[env(safe-area-inset-bottom,0px)]" tabIndex={-1}>
          {children}
        </main>
      </div>

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
    </div>
  );
};

export default Layout;

